import React, { useEffect, useState } from 'react';
import Layout from '../../../components/Layout/Layout';
import api from '../../../api/axios';
import { downloadFile, fmtDate, fmtDateTime, fmtEUR } from '../../../utils/download';
import './Billing.css';

function monthStr(offset = 0) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function CostInput({ value, onSave }) {
  const [text, setText] = useState(value != null ? String(value).replace('.', ',') : '0');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setText(value != null ? String(value).replace('.', ',') : '0');
  }, [value]);

  async function handleBlur() {
    const num = parseFloat(text.replace(',', '.'));
    if (Number.isNaN(num) || num < 0) {
      setText(value != null ? String(value).replace('.', ',') : '0');
      return;
    }
    if (num === (value || 0)) return;
    setSaving(true);
    try {
      await onSave(num);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch {
      alert('Fehler beim Speichern');
      setText(value != null ? String(value).replace('.', ',') : '0');
    } finally {
      setSaving(false);
    }
  }

  return (
    <span className="cost-input-wrap">
      <input
        type="text"
        inputMode="decimal"
        className="cost-input"
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        disabled={saving}
      />
      <span className="cost-input-suffix">€</span>
      {saved && <span className="cost-input-saved">✓</span>}
    </span>
  );
}

function LimitInput({ value, onSave, suffix = 'Lizenzen' }) {
  const [text, setText] = useState(value != null ? String(value) : '0');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setText(value != null ? String(value) : '0');
  }, [value]);

  async function handleBlur() {
    const num = parseInt(text, 10);
    if (Number.isNaN(num) || num < 1) {
      setText(value != null ? String(value) : '0');
      return;
    }
    if (num === value) return;
    setSaving(true);
    try {
      await onSave(num);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch {
      alert('Fehler beim Speichern');
      setText(value != null ? String(value) : '0');
    } finally {
      setSaving(false);
    }
  }

  return (
    <span className="cost-input-wrap">
      <input
        type="text"
        inputMode="numeric"
        className="cost-input"
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        disabled={saving}
      />
      <span className="cost-input-suffix">{suffix}</span>
      {saved && <span className="cost-input-saved">✓</span>}
    </span>
  );
}

// AM-19: Einzelaufstellung je Nutzungseinheit (aus dem Report-Payload)
function StatementDetails({ customer, month }) {
  const [open, setOpen] = useState(false);
  const licenseItems = customer.licenseItems || [];
  const assignmentItems = customer.assignmentItems || [];

  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(o => !o)}>
          {open ? 'Einzelaufstellung ausblenden' : `Einzelaufstellung anzeigen (${licenseItems.length + assignmentItems.length} Positionen)`}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => downloadFile('/billing/statement', `Nutzungseinheiten-${month}.pdf`, { month, customerId: customer.customerId, format: 'pdf' }).catch(() => alert('Export fehlgeschlagen'))}>Aufstellung als PDF</button>
        <button className="btn btn-ghost btn-sm" onClick={() => downloadFile('/billing/statement', `Nutzungseinheiten-${month}.csv`, { month, customerId: customer.customerId, format: 'csv' }).catch(() => alert('Export fehlgeschlagen'))}>Aufstellung als CSV</button>
      </div>
      {open && (
        <div className="table-container" style={{ marginTop: '0.5rem' }}>
          <table>
            <thead><tr><th>Art</th><th>Nutzer</th><th>Schulung</th><th>Von</th><th>Bis</th><th>Status</th><th style={{ textAlign: 'right' }}>Betrag</th></tr></thead>
            <tbody>
              {licenseItems.map((l, i) => (
                <tr key={`l${i}`}>
                  <td>Zugangslizenz</td><td>{l.userName}<div style={{ fontSize: '0.75rem', color: 'var(--gray-dark)' }}>{l.userEmail}</div></td><td>–</td>
                  <td>{fmtDate(l.from)}</td><td>{fmtDate(l.to)}</td><td>{l.status} ({l.days} Tage)</td>
                  <td style={{ textAlign: 'right' }}>{fmtEUR(l.amount)}</td>
                </tr>
              ))}
              {assignmentItems.map((a, i) => (
                <tr key={`a${i}`}>
                  <td>Kurslizenz</td><td>{a.userName}</td><td>{a.trainingTitle}</td>
                  <td>{fmtDate(a.assignedAt)}</td><td>{fmtDate(a.periodEnd)}</td><td>{a.status}</td>
                  <td style={{ textAlign: 'right', color: a.amount < 0 ? 'var(--primary-dark)' : 'inherit' }}>{fmtEUR(a.amount)}</td>
                </tr>
              ))}
              {licenseItems.length + assignmentItems.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray-dark)' }}>Keine Positionen</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MonthlyReport() {
  const [month, setMonth] = useState(monthStr(-1));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null); // { type, text }

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get('/billing/report', { params: { month } })
      .then(res => { if (active) setReport(res.data); })
      .catch(() => { if (active) setMsg({ type: 'error', text: 'Fehler beim Laden des Reports' }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [month]);

  async function handleSend() {
    if (!window.confirm(`Monatsabrechnung ${month} jetzt per E-Mail an alle Superadmins senden? Der Report wird dabei eingefroren.`)) return;
    setSending(true);
    setMsg(null);
    try {
      const res = await api.post('/billing/report/send', { month });
      setMsg({ type: 'success', text: `Report versendet an: ${res.data.recipients.join(', ')}` });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Fehler beim Versenden' });
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div className="billing-report-toolbar">
        <label>
          Monat:&nbsp;
          <input type="month" value={month} max={monthStr(0)} onChange={e => e.target.value && setMonth(e.target.value)} />
        </label>
        <button className="btn btn-primary btn-sm" onClick={handleSend} disabled={sending || loading}>
          {sending ? 'Wird gesendet…' : 'Report per E-Mail senden'}
        </button>
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem' }}>
        Abrechnung nach AGB § 5 (6): Zugangslizenzen je eingerichtetem Nutzer (Beendigung wirkt zum Monatsende),
        Kurslizenzen je Zuweisung mit monatlicher Rate bis zum Laufzeitende (Zuweisungsmonat pro rata).
        Rücknahmen innerhalb von 14 Tagen werden gutgeschrieben. Der Report wird am 1. jedes Monats für den Vormonat versendet und dabei eingefroren.
      </p>

      {report?.frozen && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          🔒 Eingefrorener Report (versendet {report.frozenAt ? fmtDateTime(report.frozenAt) : ''}
          {report.recipients ? ` an ${report.recipients}` : ''}). Änderungen an Daten oder Preisen wirken sich nicht mehr aus.
        </div>
      )}

      {msg && <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: '1rem' }}>{msg.text}</div>}

      {loading ? <div>Laden...</div> : report && (
        <>
          <div className="billing-summary">
            <div>
              <span className="billing-summary-label">Gesamtsumme {report.month}</span>
              <span className="billing-summary-value">{fmtEUR(report.grandTotal)}</span>
            </div>
            <div>
              <span className="billing-summary-label">Kunden</span>
              <span className="billing-summary-value">{report.customers.length}</span>
            </div>
          </div>

          {report.customers.map(c => (
            <div key={c.customerId} className="billing-card">
              <div className="billing-card-header">
                <h3>{c.name}</h3>
                <div className="billing-card-total">
                  <span>Gesamt {report.month}</span>
                  <strong>{fmtEUR(c.total)}</strong>
                </div>
              </div>

              <div className="billing-license-row">
                <span className="billing-license-label">Zugangslizenzen</span>
                <span className="billing-license-info">
                  {c.licenseActive} abgerechnet ({c.licenseNew} neu, {c.licenseEnded} beendet, Rahmen: {c.licenseLimit})
                  &times; {fmtEUR(c.licenseCostPerUser)}/Monat
                </span>
                <strong className="billing-license-sum">= {fmtEUR(c.licenseTotal)}</strong>
              </div>

              {c.trainings.length === 0 ? (
                <p className="billing-empty">Keine Kurslizenzen in diesem Monat</p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Kurslizenzen</th>
                        <th style={{ textAlign: 'center' }}>Abgerechnet</th>
                        <th style={{ textAlign: 'center' }}>Neu</th>
                        <th style={{ textAlign: 'center' }}>Entzogen</th>
                        <th style={{ textAlign: 'center' }}>Storniert</th>
                        <th style={{ textAlign: 'center' }}>Abgeschlossen</th>
                        <th style={{ textAlign: 'right' }}>Summe</th>
                        <th style={{ textAlign: 'right' }}>Gutschrift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.trainings.map(t => (
                        <tr key={t.trainingId ?? t.title}>
                          <td style={{ fontWeight: 600 }}>{t.title}</td>
                          <td style={{ textAlign: 'center' }}>{t.activeCount}</td>
                          <td style={{ textAlign: 'center' }}>{t.newCount}</td>
                          <td style={{ textAlign: 'center' }}>{t.removedCount}</td>
                          <td style={{ textAlign: 'center' }}>{t.stornoCount}</td>
                          <td style={{ textAlign: 'center' }}>{t.completedCount}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtEUR(t.amount)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--primary-dark)' }}>{t.credit ? `−${fmtEUR(t.credit)}` : '–'}</td>
                        </tr>
                      ))}
                      <tr className="billing-trainings-total">
                        <td colSpan={6}>Kurslizenzen gesamt{c.trainingsCredit ? ` (nach Gutschrift −${fmtEUR(c.trainingsCredit)})` : ''}</td>
                        <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>{fmtEUR(c.trainingsTotal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <StatementDetails customer={c} month={report.month} />
            </div>
          ))}

          {report.customers.length === 0 && <p style={{ color: 'var(--gray-dark)' }}>Noch keine Kunden vorhanden</p>}
        </>
      )}
    </>
  );
}

// AGB § 6: Fair-Use-Budgets je Kunde und KI-Modul
function AiBudgets({ customer, onChange }) {
  const [module, setModule] = useState('');
  const [budget, setBudget] = useState('');
  const [saving, setSaving] = useState(false);

  async function add(e) {
    e.preventDefault();
    const num = parseFloat(String(budget).replace(',', '.'));
    if (!module.trim() || Number.isNaN(num) || num < 0) return;
    setSaving(true);
    try {
      await api.put(`/billing/customers/${customer.id}/ai-budgets`, { module: module.trim(), monthlyBudget: num, warnThreshold: 80 });
      setModule(''); setBudget('');
      onChange();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  }

  async function save(b, value) {
    await api.put(`/billing/customers/${customer.id}/ai-budgets`, { module: b.module, monthlyBudget: value, warnThreshold: b.warnThreshold });
    onChange();
  }

  async function remove(b) {
    if (!window.confirm(`KI-Budget für Modul "${b.module}" entfernen?`)) return;
    await api.delete(`/billing/customers/${customer.id}/ai-budgets/${encodeURIComponent(b.module)}`);
    onChange();
  }

  return (
    <div style={{ marginTop: '0.75rem', borderTop: '1px solid #f0f0f0', paddingTop: '0.75rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.4rem' }}>Fair-Use-Budgets KI (§ 6), je Monat</div>
      {customer.aiBudgets.length === 0 && <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', margin: '0 0 0.5rem' }}>Kein KI-Modul gebucht.</p>}
      {customer.aiBudgets.map(b => (
        <div key={b.module} className="billing-license-row">
          <span className="billing-license-label">{b.module}</span>
          <CostInput value={b.monthlyBudget} onSave={v => save(b, v)} />
          <span className="billing-license-info">Vorwarnung bei {b.warnThreshold} %</span>
          <button className="btn btn-ghost btn-ghost-danger btn-sm" onClick={() => remove(b)}>Entfernen</button>
        </div>
      ))}
      <form onSubmit={add} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.4rem' }}>
        <input placeholder="Modul (z. B. ki-assistent)" value={module} onChange={e => setModule(e.target.value)} style={{ padding: '0.4rem 0.6rem', border: '1px solid var(--gray)', borderRadius: 6, fontFamily: 'inherit' }} />
        <input placeholder="Budget €/Monat" inputMode="decimal" value={budget} onChange={e => setBudget(e.target.value)} style={{ width: 130, padding: '0.4rem 0.6rem', border: '1px solid var(--gray)', borderRadius: 6, fontFamily: 'inherit' }} />
        <button type="submit" className="btn btn-ghost btn-sm" disabled={saving}>+ Budget anlegen</button>
      </form>
    </div>
  );
}

// § 10 (4): maschinenlesbarer Export bei Anbieterwechsel
function ExportBox({ customer }) {
  const [entity, setEntity] = useState('users');
  const entities = ['users', 'licenses', 'assignments', 'progress', 'attempts', 'certificates', 'events'];
  const labels = { users: 'Nutzer', licenses: 'Zugangslizenzen', assignments: 'Kurslizenzen', progress: 'Kursfortschritt', attempts: 'Prüfergebnisse', certificates: 'Teilnahmebestätigungen', events: 'Ereignisprotokoll' };
  const dl = params => downloadFile(`/billing/customers/${customer.id}/export`, 'Export', params).catch(() => alert('Export fehlgeschlagen'));
  return (
    <div style={{ marginTop: '0.75rem', borderTop: '1px solid #f0f0f0', paddingTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Datenexport (§ 10 (4))</span>
      <button className="btn btn-ghost btn-sm" onClick={() => dl({ format: 'json' })}>Vollexport JSON</button>
      <select value={entity} onChange={e => setEntity(e.target.value)} style={{ padding: '0.35rem 0.5rem', border: '1px solid var(--gray)', borderRadius: 6, fontFamily: 'inherit' }}>
        {entities.map(e => <option key={e} value={e}>{labels[e]}</option>)}
      </select>
      <button className="btn btn-ghost btn-sm" onClick={() => dl({ format: 'csv', entity })}>Als CSV</button>
    </div>
  );
}

function PricingOverview() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOverview();
  }, []);

  async function fetchOverview() {
    try {
      const res = await api.get('/billing/overview');
      setCustomers(res.data);
    } catch {
      setError('Fehler beim Laden der Abrechnungsdaten');
    } finally {
      setLoading(false);
    }
  }

  async function saveLicenseCost(customerId, cost) {
    await api.put(`/billing/customers/${customerId}/license-cost`, { licenseCostPerUser: cost });
    setCustomers(prev => prev.map(c =>
      c.id === customerId ? { ...c, license_cost_per_user: cost } : c
    ));
  }

  async function saveLicenseLimit(customerId, limit) {
    await api.put(`/billing/customers/${customerId}/license-limit`, { licenseLimit: limit });
    setCustomers(prev => prev.map(c =>
      c.id === customerId ? { ...c, license_limit: limit } : c
    ));
  }

  async function saveTrainingCost(customerId, trainingId, cost) {
    await api.put(`/billing/customers/${customerId}/trainings/${trainingId}/cost`, { costPerUser: cost });
    setCustomers(prev => prev.map(c =>
      c.id === customerId
        ? { ...c, trainings: c.trainings.map(t => t.training_id === trainingId ? { ...t, cost_per_user: cost } : t) }
        : c
    ));
  }

  async function saveTrainingTerm(customerId, trainingId, term) {
    await api.put(`/billing/customers/${customerId}/trainings/${trainingId}/term`, { termMonths: term });
    setCustomers(prev => prev.map(c =>
      c.id === customerId
        ? { ...c, trainings: c.trainings.map(t => t.training_id === trainingId ? { ...t, license_term_months: term } : t) }
        : c
    ));
  }

  const licenseTotal = c => (c.userCount || 0) * (c.license_cost_per_user || 0);
  const trainingsTotal = c => c.trainings.reduce((sum, t) => sum + (t.assignedCount || 0) * (t.cost_per_user || 0), 0);
  const customerTotal = c => licenseTotal(c) + trainingsTotal(c);
  const grandTotal = customers.reduce((sum, c) => sum + customerTotal(c), 0);

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}

      {loading ? <div>Laden...</div> : (
        <>
          <div className="billing-summary">
            <div>
              <span className="billing-summary-label">Laufende Kosten alle Kunden</span>
              <span className="billing-summary-value">{fmtEUR(grandTotal)} <small>/ Monat</small></span>
            </div>
            <div>
              <span className="billing-summary-label">Kunden</span>
              <span className="billing-summary-value">{customers.length}</span>
            </div>
          </div>

          {customers.map(c => (
            <div key={c.id} className="billing-card">
              <div className="billing-card-header">
                <h3>{c.name}</h3>
                <div className="billing-card-total">
                  <span>Gesamt / Monat</span>
                  <strong>{fmtEUR(customerTotal(c))}</strong>
                </div>
              </div>

              <div className="billing-license-row">
                <span className="billing-license-label">Rahmenvertrag</span>
                <LimitInput
                  value={c.license_limit}
                  onSave={limit => saveLicenseLimit(c.id, limit)}
                />
                <span className="billing-license-info">
                  {c.userCount} von {c.license_limit} vergeben{c.endingCount ? ` (${c.endingCount} enden zum Monatsende)` : ''}
                </span>
              </div>

              <div className="billing-license-row">
                <span className="billing-license-label">Lizenzkosten</span>
                <CostInput
                  value={c.license_cost_per_user}
                  onSave={cost => saveLicenseCost(c.id, cost)}
                />
                <span className="billing-license-info">
                  pro Benutzer / Monat &times; {c.userCount} Benutzer
                </span>
                <strong className="billing-license-sum">= {fmtEUR(licenseTotal(c))}</strong>
              </div>

              {c.trainings.length === 0 ? (
                <p className="billing-empty">Keine Schulungen zugewiesen</p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Modul / Schulung</th>
                        <th>Preis pro Kurslizenz / Monat</th>
                        <th>Laufzeit</th>
                        <th>Zugewiesen an</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>Summe / Monat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.trainings.map(t => (
                        <tr key={t.training_id} style={{ opacity: t.ended_at ? 0.6 : 1 }}>
                          <td style={{ fontWeight: 600 }}>{t.title}</td>
                          <td>
                            <CostInput
                              value={t.cost_per_user}
                              onSave={cost => saveTrainingCost(c.id, t.training_id, cost)}
                            />
                          </td>
                          <td>
                            <LimitInput
                              value={t.license_term_months || 12}
                              suffix="Monate"
                              onSave={term => saveTrainingTerm(c.id, t.training_id, term)}
                            />
                          </td>
                          <td>{t.assignedCount} Benutzer</td>
                          <td>
                            {t.ended_at ? <span className="badge badge-failed">Beendet {fmtDate(t.final_end)}</span>
                              : t.cancelled_at ? <span className="badge badge-pending">Gekündigt zum {fmtDate(t.final_end)}</span>
                              : <span className="badge badge-passed">Aktiv</span>}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>
                            {fmtEUR((t.assignedCount || 0) * (t.cost_per_user || 0))}
                          </td>
                        </tr>
                      ))}
                      <tr className="billing-trainings-total">
                        <td colSpan={5}>Schulungen gesamt</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtEUR(trainingsTotal(c))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <AiBudgets customer={c} onChange={fetchOverview} />
              <ExportBox customer={c} />
            </div>
          ))}

          {customers.length === 0 && (
            <p style={{ color: 'var(--gray-dark)' }}>Noch keine Kunden vorhanden</p>
          )}
        </>
      )}
    </>
  );
}

const PAYLOAD_LABELS = {
  email: 'E-Mail', role: 'Rolle', name: 'Name', changes: 'Änderungen',
  licenseEnd: 'Laufzeitende', finalEnd: 'Endet am', periodStart: 'Periodenbeginn', previousEnd: 'Bisheriges Ende', newEnd: 'Neues Ende',
  costPerMonth: 'Preis je Monat', pricePerMonth: 'Preis je Monat', termMonths: 'Laufzeit (Monate)', deadline: 'Frist',
  to: 'Empfänger', sent: 'Zugestellt', action: 'Aktion', effectiveText: 'Wirkung',
  month: 'Monat', grandTotal: 'Gesamtsumme', customers: 'Kunden', recipients: 'Empfänger', rows: 'Zeilen',
  fromUserId: 'Von (ID)', fromUserName: 'Von', toUserId: 'An (ID)', toUserName: 'An',
  accessEndedAt: 'Zugriff beendet', billingUntil: 'Vergütung bis', started: 'Begonnen', reason: 'Grund', outcome: 'Ergebnis',
  revokedTerminationAt: 'Widerrufener Termin', oldLimit: 'Alter Rahmen', newLimit: 'Neuer Rahmen',
  score: 'Ergebnis (%)', passingScore: 'Bestehensgrenze (%)', certificateId: 'Bestätigung Nr.',
  endedAt: 'Beendet am', lastLicenseEnd: 'Letzte Lizenz bis', certificatesDeleted: 'Bestätigungen gelöscht', courseLicensesClosed: 'Kurslizenzen geschlossen',
  cancelledAt: 'Gekündigt am', via: 'Über', self: 'Selbst', byAdmin: 'Durch Admin', reset: 'Zurückgesetzt',
  resetToNotStarted: 'Wieder nicht begonnen', ratio: 'Anteil', cost: 'Kosten', budget: 'Budget', trainingId: 'Schulung (ID)', frozen: 'Eingefroren',
};

function formatPayloadValue(value) {
  if (value === null || value === undefined) return '–';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return fmtDate(value);
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toLocaleString('de-DE', { maximumFractionDigits: 2 });
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    if ('from' in value && 'to' in value) return `${value.from} → ${value.to}`;
    return Object.entries(value).map(([k, v]) => `${PAYLOAD_LABELS[k] || k}: ${formatPayloadValue(v)}`).join('; ');
  }
  return String(value);
}

// Protokolldetails als lesbare Zeilen statt rohem JSON
function PayloadCell({ payload }) {
  if (!payload) return null;
  if (typeof payload !== 'object') return <span className="cell-meta">{String(payload)}</span>;
  return (
    <div className="cell-meta">
      {Object.entries(payload).map(([k, v]) => (
        <span key={k}><span style={{ color: 'var(--dark)' }}>{PAYLOAD_LABELS[k] || k}:</span> {formatPayloadValue(v)}</span>
      ))}
    </div>
  );
}

// AM-17: Append-only Ereignisprotokoll
function EventLog() {
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState({});
  const [events, setEvents] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [type, setType] = useState('');
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get('/customers').then(res => setCustomers(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get('/billing/events', { params: { customerId: customerId || undefined, type: type || undefined, limit } })
      .then(res => { if (active) { setEvents(res.data.events); setTypes(res.data.types); } })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [customerId, type, limit, summary]);

  async function runMaintenance() {
    if (!window.confirm('Lizenz-Wartungslauf jetzt ausführen? Er verarbeitet Beendigungen, Modul- und Lizenzenden, Verlängerungen und Löschfristen und läuft sonst täglich um 00:30.')) return;
    setRunning(true);
    try {
      const res = await api.post('/billing/maintenance/run');
      setSummary(res.data.summary);
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Wartungslauf');
    } finally { setRunning(false); }
  }

  const customerName = id => customers.find(c => c.id === id)?.name || (id ? `#${id}` : '–');

  return (
    <>
      <div className="billing-report-toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <select value={customerId} onChange={e => setCustomerId(e.target.value)} style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--gray)', borderRadius: 8, fontFamily: 'inherit' }}>
          <option value="">Alle Kunden</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={type} onChange={e => setType(e.target.value)} style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--gray)', borderRadius: 8, fontFamily: 'inherit' }}>
          <option value="">Alle Ereignisse</option>
          {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))} style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--gray)', borderRadius: 8, fontFamily: 'inherit' }}>
          {[100, 200, 500, 1000].map(n => <option key={n} value={n}>letzte {n}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={runMaintenance} disabled={running}>{running ? 'Läuft…' : 'Wartungslauf jetzt ausführen'}</button>
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem' }}>
        Unveränderliches Protokoll aller lizenzrelevanten Ereignisse (AVV Anlage B, AGB § 5 (6) h). Einträge können weder geändert noch gelöscht werden
        und bleiben auch nach Löschung von Konten und Kursdaten erhalten.
      </p>
      {summary && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          Wartungslauf: {summary.terminations} Zugänge beendet, {summary.moduleEnds} Module beendet, {summary.assignmentEnds} Kurslizenzen beendet,
          {' '}{summary.renewals} Verlängerungen, {summary.courseDataDeleted} Kursdaten gelöscht, {summary.accountsWarned} Löschungen angekündigt, {summary.accountsDeleted} Konten gelöscht.
        </div>
      )}
      {loading ? <div>Laden...</div> : (
        <div className="table-container">
          <table>
            <thead><tr><th>Zeitpunkt</th><th>Kunde</th><th>Ereignis</th><th>Betreff und Details</th><th>Wirkung</th><th>Akteur</th></tr></thead>
            <tbody>
              {events.map(ev => (
                <tr key={ev.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(ev.occurred_at)}</td>
                  <td>{customerName(ev.customer_id)}</td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{ev.label}</td>
                  <td>
                    <div>{ev.subject || '–'}</div>
                    <PayloadCell payload={ev.payload} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{ev.effective_at ? fmtDate(ev.effective_at) : '–'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{ev.actor_name}</td>
                </tr>
              ))}
              {events.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--gray-dark)', padding: '1.5rem' }}>Keine Ereignisse</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function Billing() {
  const [tab, setTab] = useState('report');

  return (
    <Layout title="Abrechnung">
      <div className="billing-tabs">
        <button className={`billing-tab ${tab === 'report' ? 'active' : ''}`} onClick={() => setTab('report')}>Monatsabrechnung</button>
        <button className={`billing-tab ${tab === 'pricing' ? 'active' : ''}`} onClick={() => setTab('pricing')}>Preise, Rahmenverträge & Export</button>
        <button className={`billing-tab ${tab === 'events' ? 'active' : ''}`} onClick={() => setTab('events')}>Ereignisprotokoll</button>
      </div>

      {tab === 'report' && <MonthlyReport />}
      {tab === 'pricing' && <PricingOverview />}
      {tab === 'events' && <EventLog />}
    </Layout>
  );
}
